import { reactive } from "vue";

import {
  BusinessPhoneVerificationChannel,
  BusinessPhoneVerificationStatus,
  type BusinessPhoneVerification,
} from "@/models/business-phone-verification.model";
import businessPhoneVerificationService from "@/services/business-phone-verification.service";

type VerificationChannel = {
  supported: boolean;
  selected: boolean;
  sending: boolean;
  disabled: boolean;
  get isDisabled(): boolean;
  get isSelected(): boolean
}

export type BusinessPhoneVerificationStoreState = {
  businessId: string;
  isLoading: boolean;
  isInitialised: boolean;
  channels: Record<BusinessPhoneVerificationChannel, VerificationChannel>;
  code: VerificationCode;
  check: VerificationCodeCheck;
  resend: VerificationCodeResend;
  status: BusinessPhoneVerificationStatus | null,
  get isExpired(): boolean;
}

type VerificationCode = {
  valid: boolean;
  value: string;
  get isDisabled(): boolean;
}

type VerificationCodeCheck = {
  sending: boolean;
  get isDisabled(): boolean;
}

type VerificationCodeResend = {
  sending: boolean;
  lockTTLSec: number,
  lockInterval: null | ReturnType<typeof setInterval>;
  get isDisabled(): boolean;
}

export type BusinessPhoneVerificationStore = {
  state: BusinessPhoneVerificationStoreState,
  loadPhoneVerification: typeof loadPhoneVerification;
  sendCodeViaCall: typeof sendCodeViaCall;
  sendCodeViaSMS: typeof sendCodeViaSMS;
  checkCode: typeof checkCode;
  resendCode: typeof resendCode;
}

const state: BusinessPhoneVerificationStoreState = reactive({
  businessId: "",
  isLoading: false,
  isInitialised: false,
  channels: {
    [BusinessPhoneVerificationChannel.SMS]: {
      supported: false,
      selected: false,
      sending: false,
      disabled: false,
      get isDisabled () {
        return !this.supported || this.sending || this.disabled || state.resend.lockTTLSec > 0;
      },
      get isSelected () {
        return this.selected;
      },
    },
    [BusinessPhoneVerificationChannel.CALL]: {
      supported: false,
      selected: false,
      sending: false,
      disabled: false,
      get isDisabled () {
        return !this.supported || this.sending || this.disabled  || state.resend.lockTTLSec > 0;
      },
      get isSelected () {
        return this.selected;
      },
    },
  },
  code: {
    value: "",
    valid: false,
    get isDisabled () {
      return !hasSelectedChannel() || state.isExpired;
    },
  },
  resend: {
    lockTTLSec: 0,
    lockInterval: null,
    sending: false,
    get isDisabled () {
      return this.sending || !hasSelectedChannel() || this.lockTTLSec > 0;
    },
  },
  check: {
    sending: false,
    get isDisabled () {
      return this.sending || !hasSelectedChannel() || !state.code.valid || state.isExpired;
    },
  },
  status: null,
  get isExpired () {
    return this.status === BusinessPhoneVerificationStatus.EXPIRED;
  },
});

function hasSelectedChannel () {
  const selectedChannel = getSelectedChannel();
  return selectedChannel !== null;
}

function getSelectedChannel () {
  const [ selectedChannel = null ] = (
    Object.entries(state.channels)
      .find(([ , channelFlags ]) => channelFlags.selected === true)
    || []
  );
  return selectedChannel;
}

function setResendLockTTL (ttl: number) {
  if (state.resend.lockInterval) {
    clearInterval(state.resend.lockInterval);
  }
  state.resend.lockTTLSec = ttl;

  if (state.resend.lockTTLSec > 0) {
    state.resend.lockInterval = setInterval(() => {
      try {
        if (state.resend.lockInterval !== null && state.resend.lockTTLSec <= 0) {
          if (state.isExpired) {
            state.channels.sms.selected = false;
            state.channels.call.selected = false;
            state.code.value = "";
          }
          clearInterval(state.resend.lockInterval);
        } else {
          state.resend.lockTTLSec -= 1;
        }
      } catch (e) {
        console.log(e);
      }
    }, 1e3);
  }
}

function updateStateFromVerificationResponse (verification: BusinessPhoneVerification) {
  verification.supportedChannels.forEach((channelName) => {
    state.channels[channelName].supported = true;
    state.channels[channelName].selected = channelName === verification.selectedChannel;
  });
  state.status = verification.status;
  setResendLockTTL(verification.resendLockTTLSec);
}

async function loadPhoneVerification () {
  state.isInitialised = state.isInitialised || false;
  try {
    const verification = await businessPhoneVerificationService.getStatus(state.businessId);
    updateStateFromVerificationResponse(verification);
    state.isLoading = false;
  } finally {
    state.isInitialised = true;
  }
}

async function sendCodeViaCall () {
  state.channels.call.sending = true;
  state.channels.sms.disabled = true;

  try {
    const verification = await businessPhoneVerificationService.sendCode(
      state.businessId,
      BusinessPhoneVerificationChannel.CALL
    );
    updateStateFromVerificationResponse(verification);
  } finally {
    state.channels.sms.disabled = false;
    state.channels.call.sending = false;
  }
}

async function sendCodeViaSMS () {
  state.channels.sms.sending = true;
  state.channels.call.disabled = true;

  try {
    const verification = await businessPhoneVerificationService.sendCode(
      state.businessId,
      BusinessPhoneVerificationChannel.SMS
    );
    updateStateFromVerificationResponse(verification);
  } finally {
    state.channels.call.disabled = false;
    state.channels.sms.sending = false;
  }
}

async function checkCode (code: string) {
  try {
    state.check.sending = true;

    const verification = await businessPhoneVerificationService.checkCode(
      state.businessId,
      code
    );
    updateStateFromVerificationResponse(verification);

    return {
      isValidCode: verification.status === BusinessPhoneVerificationStatus.APPROVED,
    };
  } finally {
    state.check.sending = false;
  }
}

async function resendCode () {
  try {
    state.resend.sending = true;

    const selectedChannel = getSelectedChannel();
    if (selectedChannel === BusinessPhoneVerificationChannel.SMS) {
      return await sendCodeViaSMS();
    } else if (selectedChannel === BusinessPhoneVerificationChannel.CALL) {
      return await sendCodeViaCall();
    }
    throw new Error('no channel selected');
  } finally {
    state.resend.sending = false;
  }
}

export default {
  state,
  loadPhoneVerification,
  sendCodeViaCall,
  sendCodeViaSMS,
  checkCode,
  resendCode,
};
